import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import DeleteConfirmModal from '../../components/admin/DeleteConfirmModal';
import { Size } from '../../types';
import './AdminAspectRatios.css';

interface AspectRatio {
  id: number;
  name: string;
  ratio?: number;
  defaultSizes?: Array<{ id: number; sizeId?: number; size?: Size; effectivePrice?: number }>;
  additionalSizes?: Array<{ id: number; sizeId?: number; size?: Size; effectivePrice?: number; priceOverride?: number }>;
}

interface AdditionalSize {
  sizeId: string;
}

interface AspectRatioFormData {
  name: string;
  defaultSizeIds: number[];
  additionalSizes: AdditionalSize[];
}

const AdminAspectRatios: React.FC = () => {
  const [aspectRatios, setAspectRatios] = useState<AspectRatio[]>([]);
  const [sizes, setSizes] = useState<Size[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; id: number | null; name?: string }>({ show: false, id: null });
  const [deleting, setDeleting] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<AspectRatioFormData>({
    name: '',
    defaultSizeIds: [],
    additionalSizes: []
  });
  const { success, error: showError } = useToast();

  const fetchData = useCallback(async (): Promise<void> => {
    try {
      const [aspectRatiosRes, sizesRes] = await Promise.all([
        api.get<AspectRatio[]>('/admin/aspect-ratios'),
        api.get<Size[]>('/admin/sizes')
      ]);
      setAspectRatios(aspectRatiosRes.data);
      setSizes(sizesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      showError('Failed to load data. Make sure sizes are created first in Size Management.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      if (formData.defaultSizeIds.length === 0) {
        showError('Please select at least one default size');
        return;
      }

      setSaving(true);
      const payload: any = {
        name: formData.name,
        defaultSizeIds: formData.defaultSizeIds,
        additionalSizeIds: formData.additionalSizes
          .filter(as => as.sizeId)
          .map(as => ({
            sizeId: parseInt(as.sizeId)
          }))
      };

      if (editingId) {
        payload.isActive = true;
        await api.put(`/admin/aspect-ratios/${editingId}`, payload);
        success('Aspect ratio updated');
      } else {
        await api.post('/admin/aspect-ratios', payload);
        success('Aspect ratio created');
      }

      handleCloseModal();
      fetchData();
    } catch (error) {
      console.error('Failed to save aspect ratio:', error);
      showError('Failed to save aspect ratio');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (ar: AspectRatio): void => {
    setEditingId(ar.id);
    setFormData({
      name: ar.name,
      defaultSizeIds: (ar.defaultSizes || []).map(spd => spd.sizeId || spd.size?.id || 0).filter(id => id > 0),
      additionalSizes: (ar.additionalSizes || []).map(spd => ({
        sizeId: (spd.sizeId || spd.size?.id || '').toString()
      }))
    });
    setShowModal(true);
  };

  const handleDelete = (id: number): void => {
    setDeleteConfirm({ show: true, id, name: aspectRatios.find(ar => ar.id === id)?.name });
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleteConfirm.id) return;
    try {
      setDeleting(true);
      await api.delete(`/admin/aspect-ratios/${deleteConfirm.id}`);
      success('Aspect ratio deleted');
      fetchData();
      setDeleteConfirm({ show: false, id: null });
    } catch (error) {
      console.error('Failed to delete aspect ratio:', error);
      showError('Failed to delete aspect ratio');
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = (): void => {
    setFormData({
      name: '',
      defaultSizeIds: [],
      additionalSizes: []
    });
    setEditingId(null);
  };

  const handleCloseModal = (): void => {
    setShowModal(false);
    resetForm();
  };

  const toggleDefaultSize = (sizeId: number): void => {
    const isSelected = formData.defaultSizeIds.includes(sizeId);
    if (isSelected) {
      setFormData({
        ...formData,
        defaultSizeIds: formData.defaultSizeIds.filter(id => id !== sizeId)
      });
    } else {
      setFormData({
        ...formData,
        defaultSizeIds: [...formData.defaultSizeIds, sizeId]
      });
    }
  };

  const addAdditionalSize = (): void => {
    setFormData({
      ...formData,
      additionalSizes: [...formData.additionalSizes, { sizeId: '' }]
    });
  };

  const removeAdditionalSize = (index: number): void => {
    setFormData({
      ...formData,
      additionalSizes: formData.additionalSizes.filter((_, i) => i !== index)
    });
  };

  const updateAdditionalSize = (index: number, field: keyof AdditionalSize, value: string): void => {
    const updated = [...formData.additionalSizes];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, additionalSizes: updated });
  };

  // Calculate ratio from first default size
  const calculatedRatio = formData.defaultSizeIds.length > 0
    ? (() => {
        const firstSize = sizes.find(s => s.id === formData.defaultSizeIds[0]);
        return firstSize && firstSize.ratio ? parseFloat(firstSize.ratio) : 0;
      })()
    : 0;

  // Auto-select sizes that match the calculated ratio when ratio changes (only for new aspect ratios)
  useEffect(() => {
    if (calculatedRatio > 0 && sizes.length > 0 && formData.defaultSizeIds.length === 0 && !editingId) {
      const matchingSizes = sizes.filter(s => 
        Math.abs(parseFloat(s.ratio || '0') - calculatedRatio) < 0.01
      );
      if (matchingSizes.length > 0) {
        setFormData(prev => ({
          ...prev,
          defaultSizeIds: matchingSizes.map(s => s.id)
        }));
      }
    }
  }, [calculatedRatio, editingId, formData.defaultSizeIds.length, sizes]);

  // Get available sizes for additional selection (excluding default sizes)
  const availableSizesForAdditional = sizes.filter(
    s => !formData.defaultSizeIds.includes(s.id) &&
         !formData.additionalSizes.some(as => parseInt(as.sizeId) === s.id)
  );

  if (loading) {
    return <div className="loading">Loading aspect ratios...</div>;
  }

  return (
    <div className="admin-aspect-ratios">
      <div className="container">
        <div className="page-header">
          <div className="page-header-left">
            <Link to="/admin" className="back-button">
              <i className="fas fa-arrow-left"></i> Back to Admin Panel
            </Link>
            <h1>Aspect Ratio Configuration</h1>
          </div>
          <button onClick={() => { setEditingId(null); resetForm(); setShowModal(true); }} className="btn btn-primary">
            <i className="fas fa-plus"></i> Add Aspect Ratio
          </button>
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <form onSubmit={handleSubmit} className="aspect-ratio-form">
                <h2>{editingId ? 'Edit' : 'Add'} Aspect Ratio</h2>
                
                <div className="form-group">
                  <label>Name (e.g., "1:2", "1:1", "2:3")</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="1:2"
                  />
                </div>

                <div className="form-group">
                  <label>Calculated Ratio (from first default size)</label>
                  <input
                    type="text"
                    value={(calculatedRatio || 0).toFixed(4)}
                    readOnly
                    className="readonly-input"
                  />
                  <small>Ratio is automatically calculated from the first default size</small>
                </div>

                <div className="form-group">
                  <label>Default Sizes (match ratio exactly - auto-selected based on calculated ratio)</label>
                  <div className="size-selection-grid">
                    {sizes.map((size) => {
                      const isSelected = formData.defaultSizeIds.includes(size.id);
                      const isExactMatch = calculatedRatio > 0 && Math.abs(parseFloat(size.ratio || '0') - calculatedRatio) < 0.01;
                      const shouldBeSelected = isExactMatch && calculatedRatio > 0;
                      return (
                        <label
                          key={size.id}
                          className={`size-checkbox ${isSelected ? 'selected' : ''} ${shouldBeSelected ? 'exact-match' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleDefaultSize(size.id)}
                          />
                          <span>{size.name} ({size.width}" × {size.height}")</span>
                          <small>Ratio: {parseFloat(size.ratio || '0').toFixed(4)} | Price: ${(size.price || 0).toFixed(2)}</small>
                          {shouldBeSelected && !isSelected && (
                            <small className="auto-select-hint">Matches ratio - will be auto-selected</small>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="form-group">
                  <label>Additional Sizes (sizes from size management - prices come from size defaults, can be overridden per artwork)</label>
                  {formData.additionalSizes.map((as, index) => {
                    return (
                      <div key={index} className="additional-size-row">
                        <select
                          value={as.sizeId}
                          onChange={(e) => updateAdditionalSize(index, 'sizeId', e.target.value)}
                          required
                        >
                          <option value="">Select size...</option>
                          {availableSizesForAdditional.map(size => (
                            <option key={size.id} value={size.id}>
                              {size.name} ({size.width}" × {size.height}") - Ratio: {parseFloat(size.ratio || '0').toFixed(4)} - Price: ${(size.price || 0).toFixed(2)}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => removeAdditionalSize(index)}
                          className="btn btn-danger btn-small"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    );
                  })}
                  <button type="button" onClick={addAdditionalSize} className="btn btn-outline btn-small">
                    <i className="fas fa-plus"></i> Add Additional Size
                  </button>
                  <small className="form-help-text">Note: Prices are set in Size Management. Artworks can override prices per size.</small>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving && <span className="btn-spinner" aria-hidden="true"></span>}
                    {saving ? 'Saving...' : `${editingId ? 'Update' : 'Create'} Aspect Ratio`}
                  </button>
                  <button type="button" onClick={handleCloseModal} className="btn btn-outline">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <DeleteConfirmModal
          isOpen={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ show: false, id: null })}
          onConfirm={confirmDelete}
          loading={deleting}
          itemName={deleteConfirm.name}
        />

        <div className="aspect-ratios-grid">
          {aspectRatios.map((ar) => (
            <div key={ar.id} className="aspect-ratio-card">
              <div className="aspect-ratio-header">
                <h3>{ar.name}</h3>
                <span className="ratio-value">Ratio: {(ar.ratio || 0).toFixed(4)}</span>
              </div>
              <div className="size-prices-list">
                <h4>Default Sizes (match ratio):</h4>
                {ar.defaultSizes && ar.defaultSizes.length > 0 ? (
                  ar.defaultSizes.map((spd) => (
                    <div key={spd.id} className="size-price-item default">
                      <span className="size">{spd.size?.name || 'Unknown'}</span>
                      <span className="dimensions">{spd.size?.width || 0}" × {spd.size?.height || 0}"</span>
                      <span className="price">${(spd.effectivePrice || spd.size?.price || 0).toFixed(2)}</span>
                    </div>
                  ))
                ) : (
                  <p className="no-sizes">No default sizes</p>
                )}
                {ar.additionalSizes && ar.additionalSizes.length > 0 && (
                  <>
                    <h4>Additional Sizes:</h4>
                    {ar.additionalSizes.map((spd) => (
                      <div key={spd.id} className="size-price-item additional">
                        <span className="size">{spd.size?.name || 'Unknown'}</span>
                        <span className="dimensions">{spd.size?.width || 0}" × {spd.size?.height || 0}"</span>
                        <span className="price">${(spd.size?.price || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
              <div className="aspect-ratio-actions">
                <button 
                  onClick={() => handleEdit(ar)} 
                  className="btn-icon"
                  title="Edit aspect ratio"
                  aria-label="Edit aspect ratio"
                >
                  <i className="fas fa-edit"></i>
                </button>
                <button 
                  onClick={() => handleDelete(ar.id)} 
                  className="btn-icon btn-icon-danger"
                  title="Delete aspect ratio"
                  aria-label="Delete aspect ratio"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminAspectRatios;
